import { executeGraphQL, getCurrentToken } from '@graphql-api/index.js'
import { AxiosError } from 'axios'

import type { LoginResponse, User } from '../types/types'
import { LOGIN_MUTATION, USER_QUERY } from './auth.queries.js'

/**
 * Mock authentication function for development and testing.
 *
 * This function simulates the authentication process when no real API is available.
 * It provides predefined user credentials and simulates network delay.
 */
export const mockLoginUser = async (
  username: string,
  password: string
): Promise<LoginResponse> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000))

  // Mock validation
  if (username === 'admin' && password === 'password') {
    return {
      success: true,
      data: {
        user: {
          id: '1',
          username: 'admin',
          email: 'admin@kestrix.com',
          organisationId: '1',
          role: 'SUPERADMIN',
          scope: 'GLOBAL'
        },
        token: 'mock-jwt-token',
        shouldChangePassword: false
      }
    }
  } else if (username === 'user' && password === 'password') {
    return {
      success: true,
      data: {
        user: {
          id: '2',
          username: 'user',
          email: 'user@kestrix.com',
          organisationId: '1',
          role: 'RESIDENT',
          scope: 'USER'
        },
        token: 'mock-jwt-token-user',
        shouldChangePassword: false
      }
    }
  } else {
    return {
      success: false,
      error:
        'Your username or password was not recognised. Check your details and try again.'
    }
  }
}

/**
 * Authenticates a user against the real GraphQL API.
 *
 * This function performs actual authentication by sending a GraphQL mutation
 * to the configured endpoint. It handles various error scenarios including
 * network errors, server errors, and authentication failures.
 *
 */
const realLoginUser = async (
  username: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const variables = {
      username: username || '',
      password: password || ''
    }

    const data = await executeGraphQL(LOGIN_MUTATION, variables, {
      auth: {
        kind: 'none'
      }
    })

    const login = data?.login
    const loginUser = login?.user
    const token = login?.token

    if (login && loginUser && typeof token === 'string') {
      return {
        success: true,
        data: {
          user: {
            id: loginUser.id,
            username: loginUser.username ?? '',
            email: loginUser.email ?? '',
            organisationId: loginUser.organisationId ?? '',
            role: loginUser.role as User['role'],
            scope: loginUser.scope ?? 'USER'
          },
          token,
          shouldChangePassword: login.shouldChangePassword ?? false
        }
      }
    } else {
      return {
        success: false,
        error:
          'Your username or password was not recognised. Check your details and try again.'
      }
    }
  } catch (error) {
    console.error('Login error:', error)

    // Handle different types of errors
    if (error instanceof AxiosError && error.response) {
      // Server responded with error status
      const status = error.response.status
      if (status === 403) {
        return {
          success: false,
          error:
            'Your username or password was not recognised. Check your details and try again.'
        }
      } else if (status === 401) {
        return {
          success: false,
          error: 'Authentication failed. Check your details and try again.'
        }
      } else {
        return {
          success: false,
          error: `Server error: ${status} - ${
            error.response.data?.message || 'Unknown error'
          }`
        }
      }
    } else if (error instanceof AxiosError && error.request) {
      // Network error
      return {
        success: false,
        error: 'Network error. Check your connection and try again.'
      }
    } else if (error instanceof Error) {
      // GraphQL errors from executeGraphQL (HTTP 200 with errors payload)
      return {
        success: false,
        error: error.message
      }
    } else {
      return {
        success: false,
        error: 'An unexpected error occurred'
      }
    }
  }
}

/**
 * Main authentication function that routes to either mock or real authentication.
 *
 * This function determines whether to use mock authentication (for development/testing)
 * or real authentication (for production) based on environment variables.

 */
export const loginUser = async (username: string, password: string) => {
  // Check if we should use mock authentication
  const useMockAuth =
    import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
    !import.meta.env.VITE_API_ENDPOINT

  if (useMockAuth) {
    console.log('Using mock authentication')
    return await mockLoginUser(username, password)
  } else {
    console.log('Using real authentication')
    return await realLoginUser(username, password)
  }
}

export const verifyUser = async (userId: string) => {
  try {
    const data = await executeGraphQL(
      USER_QUERY,
      { userId },
      {
        auth: {
          kind: 'bearer',
          token: getCurrentToken()
        }
      }
    )

    return {
      success: true,
      data: { data }
    }
  } catch (error) {
    console.error('User verification error:', error)
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}
